import { useState, useEffect } from 'react';
import { useUserPreferences, useStoreActions, useSettingsStore } from '@/store/useStore';
import {
  FileText, Calendar, Clock, MapPin, User, Search, Bell,
  AlertCircle, Plus, Edit2, Trash2, Check, ChevronsUpDown,
  Bus, Bot, MessageSquare, Home, ClipboardList, LayoutGrid, PackageSearch,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { DataProcessor } from '@/utils/dataProcessor';
import { findUserProgram } from '@/utils/programMatching';
import { getOrderedDays } from '@/utils/dayUtils';
import { applyTimetableProfile } from '@/utils/timetableProfiles';
import Logo from '@/components/Logo';
import { useDashboard } from '@/contexts/DashboardContext';
import SemesterMigrationNotice from '@/components/SemesterMigrationNotice';
import GraduatedTimetableState from '@/components/GraduatedTimetableState';
import { useHasGraduated } from '@/hooks/useSemesterCompletion';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { LostAndFoundShortcut } from '@/components/LostAndFoundShortcut';

// ─── Types ────────────────────────────────────────────────────────────────────
interface CustomClass {
  id: string; day: string; time: string; course: string;
  room: string; instructor: string; type: 'makeup' | 'custom'; createdAt: number;
}

// ─── Sidebar nav config ───────────────────────────────────────────────────────
const MAIN_NAV = [
  { path: '/dashboard/home',       Icon: Home,          label: 'Home'       },
  { path: '/dashboard/personal',   Icon: Calendar,      label: 'Timetable'  },
  { path: '/dashboard/browse',     Icon: LayoutGrid,    label: 'Browse'     },
  { path: '/dashboard/rooms',      Icon: MapPin,        label: 'Campus Map' },
  { path: '/dashboard/bus',        Icon: Bus,           label: 'Bus Routes' },
  { path: '/dashboard/lost-found', Icon: PackageSearch, label: 'Lost & Found' },
];
const ACADEMIC_NAV = [
  { path: '/dashboard/calendar',   Icon: Calendar,      label: 'Academics'  },
  { path: '/dashboard/exams',      Icon: FileText,      label: 'Exams'      },
  { path: '/dashboard/owlet',      Icon: Bot,           label: 'Owlet AI'   },
  { path: '/dashboard/feedback',   Icon: MessageSquare, label: 'Feedback'   },
  { path: '/dashboard/attendance', Icon: ClipboardList, label: 'Attendance' },
];
const TOP_NAV = [
  { path: '/dashboard/home',     label: 'Home'      },
  { path: '/dashboard/personal', label: 'Timetable' },
  { path: '/dashboard/browse',   label: 'Browse'    },
  { path: '/dashboard/rooms',    label: 'Rooms'     },
  { path: '/dashboard/exams',    label: 'Exams'     },
];

// ─── Primitives ───────────────────────────────────────────────────────────────
function numberToRoman(num: number | string): string {
  const n = typeof num === 'string' ? parseInt(num) : num;
  return (['','I','II','III','IV','V','VI','VII','VIII','IX','X'][n]) || String(num);
}

function SidebarLink({ path, Icon, label }: { path: string; Icon: any; label: string }) {
  const { pathname } = useLocation();
  const active = pathname === path;
  const [hov, setHov] = useState(false);
  return (
    <Link to={path} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px',
        borderRadius: 9, textDecoration: 'none', fontSize: 12, transition: '.15s',
        background: active ? 'rgba(20,184,166,.1)' : hov ? 'rgba(255,255,255,.04)' : 'transparent',
        border: `1px solid ${active ? 'rgba(20,184,166,.18)' : 'transparent'}`,
        color: active ? '#14B8A6' : hov ? 'rgba(255,255,255,.75)' : 'rgba(255,255,255,.4)',
      }}>
      <Icon size={15} aria-hidden /><span>{label}</span>
    </Link>
  );
}

function TopNavLink({ path, label }: { path: string; label: string }) {
  const { pathname } = useLocation();
  const active = pathname === path;
  const [hov, setHov] = useState(false);
  return (
    <Link to={path} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        fontSize: 12, textDecoration: 'none', transition: '.2s', paddingBottom: 2,
        color: active ? '#fff' : hov ? 'rgba(255,255,255,.65)' : 'rgba(255,255,255,.35)',
        borderBottom: active ? '1px solid rgba(20,184,166,.5)' : '1px solid transparent',
      }}>{label}</Link>
  );
}

function NavIconBtn({ children, onClick, badge }: { children: React.ReactNode; onClick?: () => void; badge?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        background: hov ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.05)',
        border: '1px solid rgba(255,255,255,.07)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : 'rgba(255,255,255,.45)',
        cursor: 'pointer', transition: '.2s', position: 'relative',
      }}>
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3, width: 14, height: 14,
          borderRadius: '50%', background: '#EF4444', border: '2px solid #0A0A0A',
          fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{
      fontFamily: "'JetBrains Mono',monospace", fontSize: 8,
      letterSpacing: '.18em', color: 'rgba(255,255,255,.2)', padding: '10px 10px 6px',
    }}>{text}</div>
  );
}

function SectionHeader({ title, meta, actions }: { title: string; meta?: string; actions?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 20, fontWeight: 400, letterSpacing: '-.01em' }}>
        {title}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {meta && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.14em', color: 'rgba(255,255,255,.25)' }}>{meta}</span>}
        {actions}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text, action }: { icon: any; text: string; action?: React.ReactNode }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
      borderRadius: 12, padding: '28px 20px', textAlign: 'center',
    }}>
      <Icon size={22} color="rgba(255,255,255,.2)" style={{ margin: '0 auto 8px', display: 'block' }} aria-hidden />
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>{text}</p>
      {action && <div style={{ marginTop: 10 }}>{action}</div>}
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, color = '#14B8A6' }: { icon: any; label: string; onClick: () => void; color?: string }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      title={label}
      style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        background: hov ? `${color}18` : 'rgba(255,255,255,.04)',
        border: `1px solid ${hov ? `${color}30` : 'rgba(255,255,255,.08)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? color : 'rgba(255,255,255,.4)', cursor: 'pointer', transition: '.2s',
      }}>
      <Icon size={14} aria-hidden />
    </button>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; border: string; color: string; label: string }> = {
    ongoing:   { bg: 'rgba(20,184,166,.1)',   border: 'rgba(20,184,166,.25)',  color: '#14B8A6',              label: 'LIVE' },
    upcoming:  { bg: 'rgba(96,165,250,.08)',  border: 'rgba(96,165,250,.2)',   color: '#60A5FA',              label: 'NEXT' },
    completed: { bg: 'rgba(255,255,255,.04)', border: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.22)', label: 'DONE' },
    cancelled: { bg: 'rgba(239,68,68,.08)',   border: 'rgba(239,68,68,.18)',   color: 'rgba(239,68,68,.65)',  label: 'OFF'  },
  };
  const s = map[status] ?? map.upcoming;
  return (
    <span style={{
      fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.07em',
      padding: '2px 7px', borderRadius: 4, fontWeight: 500, whiteSpace: 'nowrap',
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
    }}>{s.label}</span>
  );
}

// ─── Timeline card ────────────────────────────────────────────────────────────
function TimelineCard({ cls }: { cls: any }) {
  const [hov, setHov] = useState(false);
  const parts      = cls.time.split('-');
  const startRaw   = parts[0]?.trim() ?? '';
  const endRaw     = parts[1]?.trim() ?? '';
  const timeH      = startRaw.replace(/(AM|PM)/i, '').trim();
  const timeP      = startRaw.match(/(AM|PM)/i)?.[0] ?? '';

  const dotColor: Record<string, string> = {
    ongoing:   '#14B8A6',
    upcoming:  'rgba(96,165,250,.55)',
    completed: 'rgba(255,255,255,.15)',
    cancelled: 'rgba(239,68,68,.4)',
  };
  const dotGlow = cls.status === 'ongoing' ? '0 0 8px rgba(20,184,166,.55)' : 'none';

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      {/* Time col */}
      <div style={{ width: 46, flexShrink: 0, padding: '12px 8px 12px 0', textAlign: 'right' }}>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 11, lineHeight: 1,
          color: cls.status === 'ongoing' ? '#14B8A6' : 'rgba(255,255,255,.5)',
        }}>{timeH}</div>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 8,
          color: 'rgba(255,255,255,.2)', marginTop: 1,
        }}>{timeP}</div>
      </div>

      {/* Dot */}
      <div style={{
        width: 14, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        zIndex: 1, padding: '14px 0',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 2,
          background: dotColor[cls.status] ?? dotColor.upcoming,
          boxShadow: dotGlow,
        }} />
      </div>

      {/* Card */}
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          flex: 1, margin: '8px 0 8px 14px',
          background: cls.status === 'ongoing' ? 'rgba(20,184,166,.07)' : hov ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.03)',
          border: `1px solid ${cls.status === 'ongoing' ? 'rgba(20,184,166,.18)' : hov ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.06)'}`,
          borderRadius: 10, padding: '10px 14px',
          opacity: cls.status === 'completed' ? .38 : cls.status === 'cancelled' ? .32 : 1,
          transition: 'opacity .2s, background .2s, border-color .2s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
          <span style={{
            fontSize: 12, fontWeight: 500, lineHeight: 1.2, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textDecoration: cls.isCancelled ? 'line-through' : 'none',
          }}>{cls.course}</span>
          <StatusBadge status={cls.status} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {cls.instructor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>
              <User size={10} color="rgba(20,184,166,.55)" aria-hidden /> {cls.instructor}
            </div>
          )}
          {cls.room && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>
              <MapPin size={10} color="rgba(20,184,166,.55)" aria-hidden /> {cls.room}
            </div>
          )}
          {endRaw && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,.2)', fontWeight: 300, marginLeft: 'auto' }}>
              <Clock size={10} color="rgba(255,255,255,.2)" aria-hidden /> until {endRaw}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Class card (official) ────────────────────────────────────────────────────
function ClassCard({ cls, isCustom = false, onEdit, onDelete }: {
  cls: any; isCustom?: boolean; onEdit?: () => void; onDelete?: () => void;
}) {
  const accentColor = isCustom
    ? cls.type === 'makeup' ? '#F59E0B' : '#22C55E'
    : '#14B8A6';
  return (
    <div style={{
      background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
      borderLeft: `2px solid ${accentColor}`, borderRadius: 10, padding: '12px 14px',
      display: 'flex', gap: 14, alignItems: 'flex-start',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          {cls.course}
          {isCustom && (
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: 7, letterSpacing: '.08em',
              padding: '2px 6px', borderRadius: 3, flexShrink: 0,
              background: cls.type === 'makeup' ? 'rgba(245,158,11,.1)' : 'rgba(34,197,94,.1)',
              border: `1px solid ${cls.type === 'makeup' ? 'rgba(245,158,11,.25)' : 'rgba(34,197,94,.25)'}`,
              color: cls.type === 'makeup' ? '#F59E0B' : '#22C55E',
            }}>{cls.type === 'makeup' ? 'MAKEUP' : 'CUSTOM'}</span>
          )}
        </div>
        {[
          { Icon: Clock,  text: cls.time },
          { Icon: MapPin, text: cls.room },
          { Icon: User,   text: cls.instructor },
        ].filter(m => m.text).map(({ Icon, text }) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300, marginBottom: 4 }}>
            <Icon size={11} color="rgba(20,184,166,.55)" aria-hidden /> {text}
          </div>
        ))}
      </div>
      {isCustom && onEdit && onDelete && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={onEdit} title="Edit"
            style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(96,165,250,.08)', border: '1px solid rgba(96,165,250,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60A5FA', cursor: 'pointer' }}>
            <Edit2 size={11} aria-hidden />
          </button>
          <button onClick={onDelete} title="Delete"
            style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444', cursor: 'pointer' }}>
            <Trash2 size={11} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Week grid cell ───────────────────────────────────────────────────────────
function WeekCell({ cls }: { cls: any }) {
  return (
    <div style={{
      background: 'rgba(20,184,166,.07)', border: '1px solid rgba(20,184,166,.15)',
      borderRadius: 8, padding: '8px 10px', fontSize: 11,
    }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#14B8A6', marginBottom: 4 }}>
        {cls.time?.split('-')[0]?.trim()}
      </div>
      <div style={{ fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
        {cls.course}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {cls.room}
      </div>
    </div>
  );
}

// ─── Combobox field ───────────────────────────────────────────────────────────
function ComboField({ label, value, options, onChange, placeholder, required = false }: {
  label: string; value: string; options: string[]; onChange: (v: string) => void;
  placeholder?: string; required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 7 }}>
        {label}{required && <span style={{ color: 'rgba(239,68,68,.6)' }}> *</span>}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)',
            borderRadius: 9, padding: '10px 14px', fontSize: 12, color: value && value !== 'custom' ? '#fff' : 'rgba(255,255,255,.3)',
            fontFamily: "'Outfit',sans-serif", cursor: 'pointer',
          }}>
            {value && value !== 'custom' ? value : (placeholder || `Select ${label.toLowerCase()}...`)}
            <ChevronsUpDown size={13} color="rgba(255,255,255,.3)" />
          </button>
        </PopoverTrigger>
        <PopoverContent style={{ background: '#161616', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: 0 }}>
          <Command>
            <CommandInput placeholder={`Search...`} style={{ background: 'transparent', color: '#fff', fontSize: 12 }} />
            <CommandList>
              <CommandEmpty style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', padding: '12px 16px' }}>Nothing found.</CommandEmpty>
              <CommandGroup>
                {options.map(opt => (
                  <CommandItem key={opt} value={opt} onSelect={v => { onChange(v); setOpen(false); }}
                    style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', cursor: 'pointer' }}>
                    <Check size={12} className={cn('mr-2', value === opt ? 'opacity-100' : 'opacity-0')} />
                    {opt}
                  </CommandItem>
                ))}
                <CommandItem value="custom" onSelect={() => { onChange('custom'); setOpen(false); }}
                  style={{ fontSize: 12, color: 'rgba(20,184,166,.7)', cursor: 'pointer' }}>
                  <Plus size={12} className="mr-2" /> Custom…
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value === 'custom' && (
        <input placeholder={`Enter ${label.toLowerCase()}`} onChange={e => onChange(e.target.value)}
          style={{
            marginTop: 8, width: '100%', background: 'rgba(255,255,255,.05)',
            border: '1px solid rgba(20,184,166,.3)', borderRadius: 9, padding: '10px 14px',
            fontSize: 12, color: '#fff', fontFamily: "'Outfit',sans-serif", outline: 'none',
          }} />
      )}
    </div>
  );
}

// ─── Main desktop page ────────────────────────────────────────────────────────
export default function PersonalTimetableDesktop() {
  const dashboard       = useDashboard();
  const userPreferences = useUserPreferences();
  const { initializeData } = useStoreActions();
  const { preferences }    = useSettingsStore();
  const { toast }          = useToast();
  const { graduated, semesterLabel: gradSemesterLabel } = useHasGraduated(userPreferences);

  const [selectedDay,     setSelectedDay]     = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [viewMode,        setViewMode]        = useState<'day' | 'week'>('day');
  const [isLoading,       setIsLoading]       = useState(true);
  const [showMigration,   setShowMigration]   = useState(false);
  const [timetable,       setTimetable]       = useState<Record<string, any[]>>({});
  const [customClasses,   setCustomClasses]   = useState<CustomClass[]>([]);
  const [showCustom,      setShowCustom]      = useState(true);
  const [showChangeReq,   setShowChangeReq]   = useState(false);
  const [changeMsg,       setChangeMsg]       = useState('');
  const [changeEmail,     setChangeEmail]     = useState('');
  const [submitting,      setSubmitting]      = useState(false);
  const [showAddClass,    setShowAddClass]    = useState(false);
  const [editingClass,    setEditingClass]    = useState<CustomClass | null>(null);
  const [classForm,       setClassForm]       = useState({ day: '', time: '', course: '', room: '', instructor: '', type: 'makeup' as 'makeup' | 'custom' });

  const days = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
  const unreadCount = dashboard ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length : 0;

  // Load custom classes
  useEffect(() => {
    try { const s = localStorage.getItem('customClasses'); if (s) setCustomClasses(JSON.parse(s)); } catch {}
  }, []);

  const saveCustomClasses = (cls: CustomClass[]) => {
    setCustomClasses(cls); localStorage.setItem('customClasses', JSON.stringify(cls));
  };

  // DataProcessor helpers
  const dp = () => DataProcessor.getInstance();
  const getAllFromSchedule = (extractor: (cls: any) => string | undefined) => {
    const set = new Set<string>();
    dp().getAllPrograms().forEach(p => Object.values(p.schedule).forEach((day: any) => {
      if (Array.isArray(day)) day.forEach((c: any) => { const v = extractor(c); if (v && v !== 'TBA' && v !== 'Dummy Teacher' && v !== 'Online Class') set.add(v); });
    }));
    return Array.from(set).sort();
  };
  const allInstructors = () => getAllFromSchedule(c => c.instructor || c.teacher);
  const allRooms       = () => getAllFromSchedule(c => c.location || c.room);
  const allCourses     = () => getAllFromSchedule(c => c.course_name || c.course);
  const allTimeslots   = () => {
    const set = new Set<string>();
    dp().getAllPrograms().forEach(p => Object.values(p.schedule).forEach((day: any) => {
      if (Array.isArray(day)) day.forEach((c: any) => { if (c.time) set.add(c.time); });
    }));
    const parseMin = (t: string) => { const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i); if (!m) return 0; let h = +m[1]; if (m[3].toUpperCase() === 'PM' && h !== 12) h += 12; if (m[3].toUpperCase() === 'AM' && h === 12) h = 0; return h * 60 + +m[2]; };
    return Array.from(set).sort((a, b) => parseMin(a) - parseMin(b));
  };

  // Load timetable
  useEffect(() => {
    const load = async () => {
      await initializeData();
      if (userPreferences) {
        const userProgram = findUserProgram(dp().getAllPrograms(), userPreferences);
        if (userProgram) {
          const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
          const { schedule } = applyTimetableProfile(userProgram.schedule, { institute: 'Institute of Management Sciences', department: userProgram.department, program: userProgram.program, referenceDate: now });
          const allDays = getOrderedDays('Monday', true);
          const tt: Record<string, any[]> = {};
          allDays.forEach(day => {
            tt[day] = (schedule[day] || []).map((c: any) => ({ time: c.time, course: c.course_name || c.course, room: c.location, instructor: c.instructor || c.teacher, isCustom: false }));
          });
          setTimetable(tt); setShowMigration(false);
        } else { setShowMigration(true); }
      }
      setIsLoading(false);
    };
    load();
  }, [userPreferences, initializeData]);

  const getMerged = () => {
    const m = { ...timetable };
    if (showCustom) customClasses.forEach(c => {
      if (!m[c.day]) m[c.day] = [];
      m[c.day] = [...m[c.day], { ...c, isCustom: true }];
    });
    return m;
  };

  const handleChangeRequest = async () => {
    if (!changeMsg.trim()) { toast({ title: 'Message required', description: 'Please describe the issue', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('_subject', '[UniSchedule] Schedule Change Request');
      fd.append('_template', 'box'); fd.append('_captcha', 'false');
      fd.append('Student Name', userPreferences?.name || 'Anonymous');
      fd.append('Email', changeEmail || userPreferences?.email || 'Not provided');
      fd.append('Program', userPreferences?.program || 'N/A');
      fd.append('Semester', String(userPreferences?.semester || 'N/A'));
      fd.append('Group', userPreferences?.group || 'N/A');
      fd.append('Issue Description', changeMsg);
      const res = await fetch('https://formsubmit.co/ajax/info@unischedule.me', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      // FormSubmit returns HTTP 200 even on failure — the body is the source of truth.
      const result = await res.json().catch(() => ({ success: 'false' }));
      if (result.success !== 'true') throw new Error(result.message || 'Failed to submit');
      toast({ title: 'Request submitted!', description: 'Sent to info@unischedule.me' });
      setShowChangeReq(false); setChangeMsg(''); setChangeEmail('');
    } catch { toast({ title: 'Failed to submit', description: 'Please try again later', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const handleSaveCustomClass = () => {
    if (!classForm.day || !classForm.time || !classForm.course || classForm.time === 'custom' || classForm.course === 'custom') {
      toast({ title: 'Missing information', description: 'Day, time, and course are required', variant: 'destructive' }); return;
    }
    const data = { day: classForm.day, time: classForm.time, course: classForm.course, room: classForm.room === 'custom' ? '' : classForm.room, instructor: classForm.instructor === 'custom' ? '' : classForm.instructor, type: classForm.type };
    if (editingClass) {
      saveCustomClasses(customClasses.map(c => c.id === editingClass.id ? { ...data, id: c.id, createdAt: c.createdAt } : c));
      toast({ title: 'Class updated' });
    } else {
      saveCustomClasses([...customClasses, { ...data, id: Date.now().toString(), createdAt: Date.now() }]);
      toast({ title: 'Class added' });
    }
    setShowAddClass(false); setEditingClass(null);
    setClassForm({ day: '', time: '', course: '', room: '', instructor: '', type: 'makeup' });
  };

  const openEdit = (c: CustomClass) => {
    setEditingClass(c); setClassForm({ day: c.day, time: c.time, course: c.course, room: c.room, instructor: c.instructor, type: c.type }); setShowAddClass(true);
  };

  const exportToICS = () => { /* unchanged from original */ };
  const exportToPDF = () => { /* unchanged from original */ };

  if (isLoading) {
    return (
      <div className="hidden lg:flex" style={{ background: '#0A0A0A', minHeight: '100vh', alignItems: 'stretch' }}>
        <div style={{ width: 220, borderRight: '1px solid rgba(255,255,255,.07)', padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[40,32,32,32,32,32,32].map((h,i) => <div key={i} style={{ height: h, background: 'rgba(255,255,255,.04)', borderRadius: 8, animation: 'dbpulse 1.4s ease-in-out infinite', animationDelay: `${i*.08}s` }} />)}
        </div>
        <div style={{ flex: 1, padding: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[56,320,200].map((h,i) => <div key={i} style={{ height: h, background: 'rgba(255,255,255,.04)', borderRadius: 12, animation: 'dbpulse 1.4s ease-in-out infinite', animationDelay: `${i*.1}s` }} />)}
        </div>
        <style>{`@keyframes dbpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
      </div>
    );
  }
  // A graduated batch is absent from current timetable data, which also trips
  // showMigration — so the graduated state wins, otherwise finishing your
  // degree looks like a broken profile.
  if (showMigration && !graduated) return <SemesterMigrationNotice userName={userPreferences?.name} />;

  const merged     = getMerged();
  const dayClasses = merged[selectedDay] || [];
  const official   = dayClasses.filter(c => !c.isCustom);
  const custom     = dayClasses.filter(c => c.isCustom);

  return (
    <div className="hidden lg:flex" style={{ background: '#0A0A0A', fontFamily: "'Outfit',sans-serif", color: '#fff', minHeight: '100vh', flexDirection: 'column' }}>

      {/* ── Top bar ─────────────────────────────────── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 28px', borderBottom: '1px solid rgba(255,255,255,.06)',
        background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 50, flexShrink: 0,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <div style={{ display: 'flex', gap: 24 }}>
          {TOP_NAV.map(({ path, label }) => <TopNavLink key={path} path={path} label={label} />)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavIconBtn onClick={() => dashboard?.setShowSearch(true)}><Search size={14} aria-hidden /></NavIconBtn>
          <NavIconBtn badge={unreadCount} onClick={() => { if (dashboard) { dashboard.setShowNotifications(!dashboard.showNotifications); if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100); } }}><Bell size={14} aria-hidden /></NavIconBtn>
          <button onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
            style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(20,184,166,.15)', border: '1px solid rgba(20,184,166,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 500, cursor: 'pointer' }}>
            {userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}
          </button>
        </div>
      </header>

      {/* ── Body ──────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside style={{ width: 220, flexShrink: 0, borderRight: '1px solid rgba(255,255,255,.06)', padding: '20px 16px', display: 'flex', flexDirection: 'column', background: '#0A0A0A', overflowY: 'auto' }}>
          <SectionLabel text="MAIN" />
          {MAIN_NAV.map(item => <SidebarLink key={item.path} {...item} />)}
          <SectionLabel text="ACADEMICS" />
          {ACADEMIC_NAV.map(item => <SidebarLink key={item.path} {...item} />)}
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,.06)', marginTop: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(20,184,166,.15)', border: '1px solid rgba(20,184,166,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 500, flexShrink: 0 }}>
              {userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userPreferences?.name || 'Student'}</div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.06em', color: 'rgba(255,255,255,.25)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[userPreferences?.program, userPreferences?.semester ? `SEM ${numberToRoman(userPreferences.semester)}` : null, userPreferences?.group ? `GRP ${userPreferences.group}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* Page header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 28, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1, marginBottom: 4 }}>
                My Schedule
              </h1>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', color: 'rgba(255,255,255,.3)' }}>
                {[userPreferences?.program, userPreferences?.semester ? `SEMESTER ${numberToRoman(userPreferences.semester)}` : null, userPreferences?.group ? `GROUP ${userPreferences.group}` : null].filter(Boolean).join(' · ')}
              </div>
              <LostAndFoundShortcut compact />
            </div>
            {!graduated && (
              <div style={{ display: 'flex', gap: 8 }}>
                <ActionBtn icon={AlertCircle} label="Report Schedule Issue" onClick={() => setShowChangeReq(true)} color="#F59E0B" />
                <ActionBtn icon={Plus} label="Add Custom/Makeup Class" onClick={() => { setEditingClass(null); setClassForm({ day: selectedDay, time: '', course: '', room: '', instructor: '', type: 'makeup' }); setShowAddClass(true); }} color="#22C55E" />
                <ActionBtn icon={FileText} label="Export PDF" onClick={exportToPDF} />
                <ActionBtn icon={Calendar} label="Export ICS" onClick={exportToICS} />
              </div>
            )}
          </div>

          {graduated ? (
            <GraduatedTimetableState userName={userPreferences?.name} semesterLabel={gradSemesterLabel} />
          ) : (
          <>

          {/* View toggle + day selector row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* View toggle */}
            <div style={{ display: 'flex', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 9, overflow: 'hidden', flexShrink: 0 }}>
              {(['day','week'] as const).map(m => (
                <button key={m} onClick={() => setViewMode(m)}
                  style={{
                    padding: '7px 18px', border: 'none', cursor: 'pointer',
                    fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em',
                    textTransform: 'uppercase', transition: '.2s',
                    background: viewMode === m ? 'rgba(20,184,166,.15)' : 'transparent',
                    color: viewMode === m ? '#14B8A6' : 'rgba(255,255,255,.35)',
                    borderRight: m === 'day' ? '1px solid rgba(255,255,255,.07)' : 'none',
                  }}>{m}</button>
              ))}
            </div>

            {/* Day pills (day view only) */}
            {viewMode === 'day' && (
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
                {days.map(day => (
                  <button key={day} onClick={() => setSelectedDay(day)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, cursor: 'pointer', border: 'none',
                      fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.08em',
                      whiteSpace: 'nowrap', transition: '.2s',
                      background: selectedDay === day ? 'rgba(20,184,166,.15)' : 'rgba(255,255,255,.04)',
                      color: selectedDay === day ? '#14B8A6' : 'rgba(255,255,255,.4)',
                    }}>{day.substring(0,3).toUpperCase()}</button>
                ))}
              </div>
            )}

            {/* Custom classes toggle */}
            <button onClick={() => setShowCustom(!showCustom)}
              style={{
                marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.08em',
                background: showCustom ? 'rgba(20,184,166,.1)' : 'rgba(255,255,255,.04)',
                border: `1px solid ${showCustom ? 'rgba(20,184,166,.2)' : 'rgba(255,255,255,.08)'}`,
                color: showCustom ? '#14B8A6' : 'rgba(255,255,255,.35)', transition: '.2s',
              }}>{showCustom ? 'HIDE CUSTOM' : 'SHOW CUSTOM'}</button>
          </div>

          {/* ── Day view ── */}
          {viewMode === 'day' && (
            <div>
              <SectionHeader
                title="Today's Schedule"
                meta={dayClasses.length > 0 ? `${selectedDay.substring(0,3).toUpperCase()} · ${dayClasses.length} CLASSES` : undefined}
              />

              {dayClasses.length === 0 ? (
                <EmptyState icon={Calendar} text={`No classes scheduled for ${selectedDay}`} />
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Spine */}
                  <div style={{
                    position: 'absolute', left: 52, top: 14, bottom: 14,
                    width: 1, background: 'rgba(255,255,255,.06)',
                  }} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {(() => {
                      const pakistanTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
                      const currentTime = pakistanTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Karachi' });
                      const parseTimeToMinutes = (t: string) => {
                        const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
                        if (!m) return 0;
                        let h = parseInt(m[1]); const min = parseInt(m[2]); const p = m[3].toUpperCase();
                        if (p === 'PM' && h !== 12) h += 12;
                        if (p === 'AM' && h === 12) h = 0;
                        return h * 60 + min;
                      };
                      const currentMinutes = parseTimeToMinutes(currentTime);
                      
                      return dayClasses.map((cls, i) => {
                        const [st, et] = cls.time.split('-').map((t: string) => t.trim());
                        const startMinutes = parseTimeToMinutes(st);
                        const endMinutes = parseTimeToMinutes(et);
                        let status = 'upcoming';
                        if (currentMinutes >= startMinutes && currentMinutes < endMinutes) status = 'ongoing';
                        else if (currentMinutes >= endMinutes) status = 'completed';
                        return <TimelineCard key={i} cls={{ ...cls, status }} />;
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Week view ── */}
          {viewMode === 'week' && (
            <div>
              <SectionHeader title="Full Week" meta="SCHEDULE" />
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 640 }}>
                  {/* Day headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 8, marginBottom: 10 }}>
                    {days.map(day => (
                      <div key={day} style={{
                        padding: '8px 10px', borderRadius: 8, textAlign: 'center',
                        fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em',
                        background: 'rgba(255,255,255,.04)',
                        color: day === new Date().toLocaleDateString('en-US', { weekday: 'long' }) ? '#14B8A6' : 'rgba(255,255,255,.4)',
                      }}>{day.substring(0,3).toUpperCase()}</div>
                    ))}
                  </div>
                  {/* Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 8 }}>
                    {days.map(day => (
                      <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 180 }}>
                        {(timetable[day] || []).map((cls, i) => <WeekCell key={i} cls={cls} />)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          </>
          )}

        </main>
      </div>

      {/* ── Change Request Dialog ── */}
      <Dialog open={showChangeReq} onOpenChange={setShowChangeReq}>
        <DialogContent style={{ background: '#111', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, color: '#fff' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif',serif", fontWeight: 400, fontSize: 22 }}>Report Schedule Issue</DialogTitle>
            <DialogDescription style={{ color: 'rgba(255,255,255,.4)', fontSize: 12 }}>Found an error in your schedule? Let us know.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0' }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 7 }}>EMAIL (OPTIONAL)</div>
              <input type="email" value={changeEmail} onChange={e => setChangeEmail(e.target.value)} placeholder="your@email.com"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 9, padding: '10px 14px', fontSize: 12, color: '#fff', fontFamily: "'Outfit',sans-serif", outline: 'none' }} />
            </div>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 7 }}>DESCRIBE THE ISSUE *</div>
              <textarea value={changeMsg} onChange={e => setChangeMsg(e.target.value)} rows={5} placeholder="e.g. Monday 10:00 AM class should be in Room LR-105..."
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 9, padding: '10px 14px', fontSize: 12, color: '#fff', fontFamily: "'Outfit',sans-serif", outline: 'none', resize: 'none' }} />
            </div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.08em', color: 'rgba(255,255,255,.2)' }}>
              {[userPreferences?.program, `SEMESTER ${userPreferences?.semester}`, `GROUP ${userPreferences?.group}`].filter(Boolean).join(' · ')}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setShowChangeReq(false)} style={{ padding: '9px 18px', borderRadius: 9, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.5)', fontSize: 12, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
            <button onClick={handleChangeRequest} disabled={submitting} style={{ padding: '9px 18px', borderRadius: 9, background: '#14B8A6', border: 'none', color: '#0A0A0A', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", opacity: submitting ? .6 : 1 }}>{submitting ? 'Submitting…' : 'Submit Request'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add/Edit Class Dialog ── */}
      <Dialog open={showAddClass} onOpenChange={v => { setShowAddClass(v); if (!v) setEditingClass(null); }}>
        <DialogContent style={{ background: '#111', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, color: '#fff', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif',serif", fontWeight: 400, fontSize: 22 }}>{editingClass ? 'Edit' : 'Add'} Custom Class</DialogTitle>
            <DialogDescription style={{ color: 'rgba(255,255,255,.4)', fontSize: 12 }}>Makeup classes or custom classes only visible to you</DialogDescription>
          </DialogHeader>
          <div style={{ padding: '8px 0' }}>
            {/* Type */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 7 }}>CLASS TYPE</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['makeup','custom'] as const).map(t => (
                  <button key={t} onClick={() => setClassForm(f => ({ ...f, type: t }))}
                    style={{ flex: 1, padding: '9px', borderRadius: 9, cursor: 'pointer', border: `1px solid ${classForm.type === t ? (t === 'makeup' ? 'rgba(245,158,11,.4)' : 'rgba(34,197,94,.4)') : 'rgba(255,255,255,.08)'}`, background: classForm.type === t ? (t === 'makeup' ? 'rgba(245,158,11,.1)' : 'rgba(34,197,94,.1)') : 'rgba(255,255,255,.03)', color: classForm.type === t ? (t === 'makeup' ? '#F59E0B' : '#22C55E') : 'rgba(255,255,255,.4)', fontSize: 12, fontFamily: "'Outfit',sans-serif", transition: '.2s' }}>
                    {t === 'makeup' ? 'Makeup Class' : 'Custom Class'}
                  </button>
                ))}
              </div>
            </div>
            {/* Day */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 7 }}>DAY <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                  <button key={d} onClick={() => setClassForm(f => ({ ...f, day: d }))}
                    style={{ padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '.06em', border: `1px solid ${classForm.day === d ? 'rgba(20,184,166,.35)' : 'rgba(255,255,255,.08)'}`, background: classForm.day === d ? 'rgba(20,184,166,.12)' : 'rgba(255,255,255,.03)', color: classForm.day === d ? '#14B8A6' : 'rgba(255,255,255,.35)', transition: '.15s' }}>
                    {d.substring(0,3)}
                  </button>
                ))}
              </div>
            </div>
            <ComboField label="Time" value={classForm.time} options={allTimeslots()} onChange={v => setClassForm(f => ({ ...f, time: v }))} required />
            <ComboField label="Course" value={classForm.course} options={allCourses()} onChange={v => setClassForm(f => ({ ...f, course: v }))} required />
            <ComboField label="Room" value={classForm.room} options={allRooms()} onChange={v => setClassForm(f => ({ ...f, room: v }))} />
            <ComboField label="Instructor" value={classForm.instructor} options={allInstructors()} onChange={v => setClassForm(f => ({ ...f, instructor: v }))} />
          </div>
          <DialogFooter>
            <button onClick={() => { setShowAddClass(false); setEditingClass(null); }} style={{ padding: '9px 18px', borderRadius: 9, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.5)', fontSize: 12, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
            <button onClick={handleSaveCustomClass} style={{ padding: '9px 18px', borderRadius: 9, background: '#14B8A6', border: 'none', color: '#0A0A0A', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>{editingClass ? 'Update' : 'Add'} Class</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`@keyframes dbpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
    </div>
  );
}
